<template>
  <section class="company-view-shell">
    <div v-if="isInitialLoading" class="company-view-loader company-view-loader--inline">
      <div class="company-view-spinner">
        <span class="company-view-spinner-ring"></span>
        <span class="company-view-spinner-core"></span>
      </div>
      <p class="company-view-loader-title">Cargando cliente</p>
      <p class="company-view-loader-copy">Preparando la ficha principal y sus relaciones.</p>
    </div>

    <template v-else>
      <div :class="{ 'company-view-content--loading': isRefreshing }">
        <CompanyWorkspaceHeader
          :company="company"
          :record="record"
          :completion="completion"
          :can-edit-company="canEditRelatedSections"
          :can-change-status="hasStatusPermission"
          @edit-record="openDrawer('record')"
          @change-status="openDrawer('record')"
          @add-related="goToRelatedTab"

        />

        <CompanyReviewPanel v-if="company.id" :key="reviewRefreshKey" :company-id="company.id" @changed="loadWorkspace" />
        <div class="company-workspace__body">
          <CompanySummaryRail
            :completion="completion"
            :stats="summaryStats"
            :health="healthSummary"
            :last-activity="lastActivity"
          />

          <main class="company-workspace__main">
            <CompanyWorkspaceNav
              :items="workspaceTabsWithCounts"
              :active-tab="activeTab"
              @change="activeTab = $event"
            />

            <section class="company-workspace__content">
              <CompanyOverviewPanel
                v-if="activeTab === 'overview'"
                :company="company"
                :record="record"
                :stats="summaryStats"
                :health="healthSummary"
                :activity="activityFeed"
                :pending-items="pendingItems"
                @edit-record="openDrawer('record')"
                @open-module="activeTab = $event"
                @view-activity-history="openActivityHistory"
              />

              <CompanyPeoplePanel
                v-else-if="activeTab === 'people'"
                :contacts="contacts"
                :partners="partners"
                :legal-representatives="legalRepresentatives"
                :other-relationships="otherRelationships"
                :loading="isPeopleLoading"
                :permissions="peoplePermissions"
                @create-contact="openDrawer('contact')"
                @edit-contact="openDrawer('contact', $event)"
                @delete-contact="confirmDeleteContact($event)"
                @create-partner="openDrawer('partner')"
                @edit-partner="openDrawer('partner', $event)"
                @delete-partner="confirmDeletePartner($event)"
                @create-legal-representative="openDrawer('legal-representative')"
                @edit-legal-representative="openDrawer('legal-representative', $event)"
                @delete-legal-representative="confirmDeleteLegalRepresentative($event)"
                @create-other-relationship="openDrawer('other-relationship')"
                @edit-other-relationship="openDrawer('other-relationship', $event)"
                @delete-other-relationship="confirmDeleteOtherRelationship($event)"
              />

              <CompanyOperationsPanel
                v-else-if="activeTab === 'operations'"
                :company="company"
                :addresses="addresses"
                :contracts="contracts"
                :bank-accounts="bankAccounts"
                :record="record"
                :loading="isOperationsLoading"
                :permissions="operationsPermissions"
                @create-address="openDrawer('address')"
                @edit-address="openDrawer('address', $event)"
                @delete-address="confirmDeleteAddress($event)"
                @create-contract="openDrawer('contract')"
                @edit-contract="openDrawer('contract', $event)"
                @delete-contract="confirmDeleteContract($event)"
                @create-bank-account="openDrawer('bank-account')"
                @edit-bank-account="openDrawer('bank-account', $event)"
                @delete-bank-account="confirmDeleteBankAccount($event)"
                @edit-codes="openDrawer('record')"
              />

              <CompanyAccessPanel
                v-else-if="activeTab === 'access'"
                :secrets="secrets"
                :loading="isAccessLoading"
                :permissions="accessPermissions"
                :rotation-alerts="rotationAlerts"
                @create-secret="openDrawer('secret')"
                @edit-secret="openDrawer('secret', $event)"
                @reveal-secret="revealSecret($event)"
                @copy-secret="copySecret($event)"
                @view-audit="openSecretAuditHistory"
                @delete-secret="confirmDeleteSecret($event)"
              />

              <CompanyLegalPanel
                v-else-if="activeTab === 'legal'"
                :company="company"
                :record="record"
                :pending-items="pendingItems"
                :permissions="legalPermissions"
                @edit-record="openDrawer('record')"
              />

              <CompanyTrackingPanel
                v-else-if="activeTab === 'tracking'"
                :company-id="company.id"
                :notes="notes"
                :tasks="tasks"
                :users="assignableUsers"
                :current-user-id="currentUserId"
                :can-manage-users="canManageUsers"
                :highlighted-task-id="highlightedTaskId"
                @refresh="loadTracking"
              />
            </section>
          </main>
        </div>
      </div>
    </template>

    <div v-if="isRefreshing && !isInitialLoading" class="company-view-loader company-view-loader--overlay">
      <div class="company-view-spinner company-view-spinner--compact">
        <span class="company-view-spinner-ring"></span>
        <span class="company-view-spinner-core"></span>
      </div>
      <p class="company-view-loader-title">Actualizando cliente</p>
    </div>

    <EntityDrawer
      :open="drawer.open"
      :mode="drawer.mode"
      :title="drawerTitle"
      :loading="drawerLoading"
      @close="closeDrawer"
    >
      <RecordForm
        v-if="drawer.type === 'record' && !drawerLoading"
        :taxRegimes="taxRegimes"
        :billerTypes="billerTypes"
        :company="company"
        :recordProp="record"
        :redirectOnSave="false"
        @saved="handleRecordSaved"
      />

      <ContactEditor
        v-if="drawer.type === 'contact' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        @saved="handleContactSaved"
        @cancel="closeDrawer"
      />

      <AddressEditor
        v-if="drawer.type === 'address' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        :comunas="comunas"
        @saved="handleAddressSaved"
        @cancel="closeDrawer"
      />

      <BankAccountEditor
        v-if="drawer.type === 'bank-account' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        @saved="handleBankAccountSaved"
        @cancel="closeDrawer"
      />

      <PartnerProfileEditor
        ref="partnerProfileEditor"
        v-if="drawer.type === 'partner' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        :permissions="partnerProfilePermissions"
        @saved="handlePartnerProfileSaved"
        @refresh="handlePartnerProfileRefresh"
        @reveal-secret="revealSecret($event)"
        @copy-secret="copySecret($event)"
        @delete-secret="confirmDeleteSecret($event)"
        @cancel="closeDrawer"
      />

      <LegalRepresentativeEditor
        v-if="drawer.type === 'legal-representative' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        @saved="handleLegalRepresentativeSaved"
        @cancel="closeDrawer"
      />

      <OtherRelationshipEditor
        v-if="drawer.type === 'other-relationship' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        @saved="handleOtherRelationshipSaved"
        @cancel="closeDrawer"
      />

      <ContractEditor
        v-if="drawer.type === 'contract' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        :currencies="currencies"
        @saved="handleContractSaved"
        @cancel="closeDrawer"
      />

      <SecretEditor
        v-if="drawer.type === 'secret' && !drawerLoading"
        :value="drawer.entity"
        :company-id="company.id"
        @saved="handleSecretSaved"
        @cancel="closeDrawer"
      />

      <div v-if="drawer.type === 'activity-history'" class="company-activity-history">
        <div v-if="isActivityHistoryLoading" class="company-activity-history__state">
          Cargando historial...
        </div>

        <div v-else-if="activityHistory.length" class="company-activity-history__list">
          <article v-for="entry in activityHistory" :key="entry.id" class="company-activity-history__item">
            <div>
              <p class="company-activity-history__title">{{ entry.title }}</p>
              <p v-if="entry.meta" class="company-activity-history__meta">{{ entry.meta }}</p>
            </div>
            <p class="company-activity-history__date">{{ formatActivityDate(entry.created_at) }}</p>
          </article>
        </div>

        <div v-else class="company-activity-history__state">
          No hay actividad historica registrada aun. Los movimientos comenzaran a verse desde esta implementacion.
        </div>

        <div v-if="activityHistoryTotal > activityHistoryPageSize" class="company-activity-history__actions">
          <button class="company-activity-history__button" type="button" :disabled="activityHistoryPage <= 1 || isActivityHistoryLoading" @click="changeActivityHistoryPage(activityHistoryPage - 1)">
            Anterior
          </button>
          <span class="company-activity-history__page">Pagina {{ activityHistoryPage }}</span>
          <button class="company-activity-history__button" type="button" :disabled="!activityHistoryHasNextPage || isActivityHistoryLoading" @click="changeActivityHistoryPage(activityHistoryPage + 1)">
            Siguiente
          </button>
        </div>
      </div>

      <div v-if="drawer.type === 'secret-audit-history'" class="company-activity-history">
        <div class="company-activity-history__toolbar">
          <label class="company-activity-history__toggle">
            <input v-model="includePeopleInSecretAudit" type="checkbox" @change="reloadSecretAuditHistory">
            <span>Incluir secretos de personas relacionadas</span>
          </label>

          <button class="company-activity-history__button" type="button" :disabled="isSecretAuditLoading || isExportingSecretAudit" @click="exportSecretAuditHistory">
            {{ isExportingSecretAudit ? 'Exportando...' : 'Exportar auditoria' }}
          </button>
        </div>

        <div v-if="isSecretAuditLoading" class="company-activity-history__state">
          Cargando auditoria...
        </div>

        <div v-else-if="secretAuditHistory.length" class="company-activity-history__list">
          <article v-for="entry in secretAuditHistory" :key="entry.id" class="company-activity-history__item">
            <div>
              <p class="company-activity-history__title">
                {{ formatSecretActionLabel(entry) }} · {{ entry.meta || entry.payload.secret_name || 'Secreto' }}
              </p>
              <p class="company-activity-history__meta">
                {{ entry.created_user_name || 'Usuario no identificado' }} · {{ formatSecretScopeLabel(entry) }}
              </p>
              <p v-if="entry.payload && entry.payload.person_name" class="company-activity-history__meta">
                Persona: {{ entry.payload.person_name }}
              </p>
              <p v-if="entry.payload && entry.payload.reason" class="company-activity-history__meta">
                Motivo: {{ entry.payload.reason }}
              </p>
            </div>
            <p class="company-activity-history__date">{{ formatActivityDate(entry.created_at) }}</p>
          </article>
        </div>

        <div v-else class="company-activity-history__state">
          No hay auditoria registrada para exposiciones de secretos.
        </div>

        <div v-if="secretAuditHistoryTotal > secretAuditHistoryPageSize" class="company-activity-history__actions">
          <button class="company-activity-history__button" type="button" :disabled="secretAuditHistoryPage <= 1 || isSecretAuditLoading" @click="changeSecretAuditHistoryPage(secretAuditHistoryPage - 1)">
            Anterior
          </button>
          <span class="company-activity-history__page">Pagina {{ secretAuditHistoryPage }}</span>
          <button class="company-activity-history__button" type="button" :disabled="!secretAuditHistoryHasNextPage || isSecretAuditLoading" @click="changeSecretAuditHistoryPage(secretAuditHistoryPage + 1)">
            Siguiente
          </button>
        </div>
      </div>
    </EntityDrawer>

    <ConfirmDialog
      :open="confirmDialog.open"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :loading="confirmDialog.loading"
      :show-confirm="Boolean(confirmDialog.action)"
      :cancel-label="confirmDialog.action ? 'Cancelar' : 'Cerrar'"
      @confirm="executeConfirmAction"
      @cancel="closeConfirmDialog"
    />

    <transition name="confirm-dialog-fade">
      <div v-if="reasonDialog.open" class="confirm-dialog">
        <div class="confirm-dialog__backdrop" @click="closeReasonDialog"></div>

        <div class="confirm-dialog__panel" role="dialog" aria-modal="true" :aria-label="reasonDialog.title">
          <header class="confirm-dialog__head">
            <div>
              <p class="confirm-dialog__eyebrow">Auditoria</p>
              <h2 class="confirm-dialog__title">{{ reasonDialog.title }}</h2>
            </div>

            <button class="confirm-dialog__close" type="button" aria-label="Cerrar" @click="closeReasonDialog">
              <i class="fa fa-times"></i>
            </button>
          </header>

          <section class="confirm-dialog__body">
            <p class="confirm-dialog__message">{{ reasonDialog.message }}</p>
            <textarea
              v-model="reasonDialog.reason"
              class="company-view-reason__textarea"
              rows="4"
              maxlength="500"
              placeholder="Describe por que necesitas exponer este secreto."
            ></textarea>
            <input
              v-model="reasonDialog.password"
              class="company-view-reason__input"
              type="password"
              autocomplete="current-password"
              placeholder="Confirma tu contraseña"
            >
            <p v-if="reasonDialog.error" class="company-view-reason__error">{{ reasonDialog.error }}</p>
          </section>

          <footer class="confirm-dialog__foot">
            <button class="confirm-dialog__ghost" type="button" :disabled="reasonDialog.loading" @click="closeReasonDialog">
              Cancelar
            </button>
            <button class="confirm-dialog__danger" type="button" :disabled="reasonDialog.loading" @click="submitReasonDialog">
              {{ reasonDialog.loading ? 'Procesando...' : reasonDialog.confirmLabel }}
            </button>
          </footer>
        </div>
      </div>
    </transition>
  </section>
</template>
<script>
import CompanyReviewPanel from '@/components/company/CompanyReviewPanel.vue'
import CompanyWorkspaceHeader from '@/components/company/CompanyWorkspaceHeader.vue'
import CompanyWorkspaceNav from '@/components/company/CompanyWorkspaceNav.vue'
import CompanySummaryRail from '@/components/company/CompanySummaryRail.vue'
import CompanyOverviewPanel from '@/components/company/panels/CompanyOverviewPanel.vue'
import CompanyPeoplePanel from '@/components/company/panels/CompanyPeoplePanel.vue'
import CompanyOperationsPanel from '@/components/company/panels/CompanyOperationsPanel.vue'
import CompanyAccessPanel from '@/components/company/panels/CompanyAccessPanel.vue'
import CompanyLegalPanel from '@/components/company/panels/CompanyLegalPanel.vue'
import CompanyTrackingPanel from '@/components/company/panels/CompanyTrackingPanel.vue'
import RecordForm from '@/components/company/RecordForm.vue'
import ContactEditor from '@/components/company/editors/ContactEditor.vue'
import PartnerProfileEditor from '@/components/company/editors/PartnerProfileEditor.vue'
import AddressEditor from '@/components/company/editors/AddressEditor.vue'
import BankAccountEditor from '@/components/company/editors/BankAccountEditor.vue'
import LegalRepresentativeEditor from '@/components/company/editors/LegalRepresentativeEditor.vue'
import OtherRelationshipEditor from '@/components/company/editors/OtherRelationshipEditor.vue'
import ContractEditor from '@/components/company/editors/ContractEditor.vue'
import SecretEditor from '@/components/company/editors/SecretEditor.vue'
import EntityDrawer from '@/components/shared/EntityDrawer.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import companyService from '@/services/company/company.service'
import companyPeopleService from '@/services/company/companyPeople.service'
import companyOperationsService from '@/services/company/companyOperations.service'
import companyAccessService from '@/services/company/companyAccess.service'
import auth from '@/auth/index'
import trackingService from '@/services/tracking.service'

export default {
  name: 'CompanyView',
  components: {
    CompanyReviewPanel,
    CompanyWorkspaceHeader,
    CompanyWorkspaceNav,
    CompanySummaryRail,
    CompanyOverviewPanel,
    CompanyPeoplePanel,
    CompanyOperationsPanel,
    CompanyAccessPanel,
    CompanyLegalPanel,
    CompanyTrackingPanel,
    RecordForm,
    ContactEditor,
    PartnerProfileEditor,
    AddressEditor,
    BankAccountEditor,
    LegalRepresentativeEditor,
    OtherRelationshipEditor,
    ContractEditor,
    SecretEditor,
    EntityDrawer,
    ConfirmDialog
  },
  data: () => ({
    company: {
      id: 0
    },
    record: {
      id: 0
    },
    dataQuality: {
      percentage: 0,
      completed_checks: 0,
      total_checks: 0,
      missing: [],
      tone: 'critical'
    },
    invoices: [],
    currencies: [],
    taxRegimes: [],
    billerTypes: [],
    contacts: [],
    partners: [],
    legalRepresentatives: [],
    otherRelationships: [],
    addresses: [],
    contracts: [],
    bankAccounts: [],
    comunas: [],
    secrets: [],
    notes: [],
    tasks: [],
    assignableUsers: [],
    isInitialLoading: true,
    isRefreshing: false,
    isRecordCatalogLoading: false,
    isPeopleLoading: false,
    isOperationsLoading: false,
    isAccessLoading: false,
    isTrackingLoading: false,
    activeTab: 'overview',
    activityFeed: [],
    activityTotal: 0,
    activityHistory: [],
    activityHistoryTotal: 0,
    activityHistoryPage: 1,
    activityHistoryPageSize: 20,
    isActivityHistoryLoading: false,
    secretAuditHistory: [],
    secretAuditHistoryTotal: 0,
    secretAuditHistoryPage: 1,
    secretAuditHistoryPageSize: 20,
    isSecretAuditLoading: false,
    isExportingSecretAudit: false,
    includePeopleInSecretAudit: true,
    drawer: {
      open: false,
      type: '',
      mode: 'create',
      entity: null
    },
    confirmDialog: {
      open: false,
      title: '',
      message: '',
      loading: false,
      action: '',
      payload: null
    },
    reasonDialog: {
      open: false,
      title: '',
      message: '',
      confirmLabel: 'Confirmar',
      action: '',
      secret: null,
      reason: '',
      password: '',
      error: '',
      loading: false
    }
  }),
  computed: {
    reviewRefreshKey () { return `${this.company.id}:${this.company.review_version}:${this.company.review_status}` },
    hasStatusPermission () {
      return this.hasPermission('EDITFORM')
    },
    canViewAccessValues () {
      return this.hasPermission('REVEAL_SECRET_VALUE')
    },
    canManageUsers () {
      return auth.canManageUsers()
    },
    currentUserId () {
      const user = auth.getUserLogged() || {}
      return user.id || 0
    },
    highlightedTaskId () {
      return this.$route.query.task || ''
    },
    canEditRelatedSections () {
      return this.hasPermission('EDITFORM') && this.company.status !== 'Inactivo'
    },
    peoplePermissions () {
      return {
        canCreate: this.canEditRelatedSections,
        canEdit: this.canEditRelatedSections,
        canDelete: this.canEditRelatedSections
      }
    },
    operationsPermissions () {
      return {
        canCreate: this.canEditRelatedSections,
        canEdit: this.canEditRelatedSections,
        canDelete: this.canEditRelatedSections
      }
    },
    accessPermissions () {
      return {
        canCreate: this.canEditRelatedSections,
        canEdit: this.canEditRelatedSections,
        canDelete: this.canEditRelatedSections,
        canRevealSecret: this.canViewAccessValues
      }
    },
    partnerProfilePermissions () {
      return {
        canCreateSecret: this.canEditRelatedSections,
        canEditSecret: this.canEditRelatedSections,
        canDeleteSecret: this.canEditRelatedSections,
        canRevealSecret: this.canViewAccessValues
      }
    },
    legalPermissions () {
      return {
        canEdit: this.canEditRelatedSections
      }
    },
    rotationAlerts () {
      return this.secrets.filter(secret => secret.needs_rotation)
    },
    drawerLoading () {
      return this.drawer.type === 'record' ? this.isRecordCatalogLoading : false
    },
    completion () {
      return {
        percentage: this.dataQuality && typeof this.dataQuality.percentage === 'number' ? this.dataQuality.percentage : 0,
        missing: this.pendingItems
      }
    },
    pendingItems () {
      if (!this.dataQuality || !Array.isArray(this.dataQuality.missing)) return []
      return this.dataQuality.missing.slice(0, 3)
    },
    summaryStats () {
      return {
        contacts: this.contacts.length,
        addresses: this.addresses.length,
        partners: this.partners.length,
        legalRepresentatives: this.legalRepresentatives.length,
        otherRelationships: this.otherRelationships.length,
        secrets: this.secrets.length,
        contracts: this.contracts.length,
        bankAccounts: this.bankAccounts.length,
        notes: this.notes.length,
        tasks: this.tasks.length
      }
    },
    healthSummary () {
      if (this.pendingItems.length >= 3) {
        return { tone: 'warning', label: 'Hay datos clave pendientes' }
      }
      return { tone: 'good', label: 'Ficha operativa estable' }
    },
    lastActivity () {
      return this.activityFeed.length ? this.activityFeed[0] : null
    },
    activityHistoryHasNextPage () {
      return this.activityHistoryPage * this.activityHistoryPageSize < this.activityHistoryTotal
    },
    secretAuditHistoryHasNextPage () {
      return this.secretAuditHistoryPage * this.secretAuditHistoryPageSize < this.secretAuditHistoryTotal
    },
    workspaceTabsWithCounts () {
      return [
        { id: 'overview', label: 'Overview' },
        { id: 'people', label: 'Personas', count: this.summaryStats.contacts + this.summaryStats.partners + this.summaryStats.legalRepresentatives + this.summaryStats.otherRelationships },
        { id: 'operations', label: 'Operación', count: this.summaryStats.addresses + this.summaryStats.contracts + this.summaryStats.bankAccounts },
        { id: 'legal', label: 'Legal y Fiscal' },
        { id: 'access', label: 'Accesos', count: this.summaryStats.secrets },
        { id: 'tracking', label: 'Seguimiento', count: this.summaryStats.notes + this.summaryStats.tasks }
      ]
    },
    activeTabLabel () {
      const tab = this.workspaceTabsWithCounts.find(item => item.id === this.activeTab)
      return tab ? tab.label : 'Workspace'
    },
    drawerTitle () {
      if (this.drawer.type === 'record') {
        return this.record.id ? 'Editar ficha del cliente' : 'Completar ficha del cliente'
      }
      if (this.drawer.type === 'contact') {
        return this.drawer.mode === 'edit' ? 'Editar contacto' : 'Nuevo contacto'
      }
      if (this.drawer.type === 'address') {
        return this.drawer.mode === 'edit' ? 'Editar dirección' : 'Nueva dirección'
      }
      if (this.drawer.type === 'bank-account') {
        return this.drawer.mode === 'edit' ? 'Editar cuenta bancaria' : 'Nueva cuenta bancaria'
      }
      if (this.drawer.type === 'partner') {
        return this.drawer.mode === 'edit' ? 'Ficha de socio' : 'Nuevo socio'
      }
      if (this.drawer.type === 'legal-representative') {
        return this.drawer.mode === 'edit' ? 'Editar representante legal' : 'Nuevo representante legal'
      }
      if (this.drawer.type === 'other-relationship') {
        return this.drawer.mode === 'edit' ? 'Editar otra relacion' : 'Nueva otra relacion'
      }
      if (this.drawer.type === 'contract') {
        return this.drawer.mode === 'edit' ? 'Editar contrato' : 'Nuevo contrato'
      }
      if (this.drawer.type === 'secret') {
        return this.drawer.mode === 'edit' ? 'Editar acceso' : 'Nuevo acceso'
      }
      if (this.drawer.type === 'activity-history') {
        return 'Historial de movimientos'
      }
      if (this.drawer.type === 'secret-audit-history') {
        return 'Auditoria de accesos'
      }
      return 'Editar'
    }
  },
  methods: {
    formatActivityDate (value) {
      return value ? new Date(value).toLocaleString() : ''
    },
    async loadActivityHistory (page = 1) {
      this.isActivityHistoryLoading = true
      try {
        const response = await companyService.getActivityHistory(parseInt(this.$route.params.id), page, this.activityHistoryPageSize)
        this.activityHistory = response.items
        this.activityHistoryTotal = response.total
        this.activityHistoryPage = response.page
      } catch (error) {
        console.log(error)
      } finally {
        this.isActivityHistoryLoading = false
      }
    },
    async loadSecretAuditHistory (page = 1) {
      this.isSecretAuditLoading = true
      try {
        const response = await companyService.getSecretExposureAudit(
          parseInt(this.$route.params.id),
          page,
          this.secretAuditHistoryPageSize,
          this.includePeopleInSecretAudit
        )
        this.secretAuditHistory = response.items
        this.secretAuditHistoryTotal = response.total
        this.secretAuditHistoryPage = response.page
      } catch (error) {
        console.log(error)
      } finally {
        this.isSecretAuditLoading = false
      }
    },
    async openActivityHistory () {
      this.drawer = {
        open: true,
        type: 'activity-history',
        mode: 'view',
        entity: null
      }
      await this.loadActivityHistory(1)
    },
    async openSecretAuditHistory () {
      this.drawer = {
        open: true,
        type: 'secret-audit-history',
        mode: 'view',
        entity: null
      }
      await this.loadSecretAuditHistory(1)
    },
    async changeActivityHistoryPage (page) {
      await this.loadActivityHistory(page)
    },
    async changeSecretAuditHistoryPage (page) {
      await this.loadSecretAuditHistory(page)
    },
    async reloadSecretAuditHistory () {
      await this.loadSecretAuditHistory(1)
    },
    async loadPeople () {
      this.isPeopleLoading = true
      try {
        const people = await companyPeopleService.getPeople(parseInt(this.$route.params.id))
        this.contacts = people.contacts
        this.partners = people.partners
        this.legalRepresentatives = people.legalRepresentatives
        this.otherRelationships = people.otherRelationships
      } catch (error) {
        console.log(error)
      } finally {
        this.isPeopleLoading = false
      }
    },
    async loadOperations () {
      this.isOperationsLoading = true
      try {
        const operations = await companyOperationsService.getOperations(parseInt(this.$route.params.id))
        this.addresses = operations.addresses
        this.contracts = operations.contracts
        this.bankAccounts = operations.bankAccounts
        this.comunas = operations.comunas
      } catch (error) {
        console.log(error)
      } finally {
        this.isOperationsLoading = false
      }
    },
    async loadAccesses () {
      this.isAccessLoading = true
      try {
        const access = await companyAccessService.getAccesses(parseInt(this.$route.params.id))
        this.secrets = access.secrets
      } catch (error) {
        console.log(error)
      } finally {
        this.isAccessLoading = false
      }
    },
    async loadAssignableUsers () {
      if (!this.canManageUsers || this.assignableUsers.length) return
      try {
        const response = await this.$axios.get('user/getUsers')
        this.assignableUsers = response.data.users || []
      } catch (error) {
        console.log(error)
      }
    },
    async loadTracking () {
      this.isTrackingLoading = true
      try {
        const [notes, tasks] = await Promise.all([
          trackingService.getCompanyNotes(parseInt(this.$route.params.id)),
          trackingService.getCompanyTasks(parseInt(this.$route.params.id))
        ])
        this.notes = notes
        this.tasks = tasks
        await this.loadAssignableUsers()
      } catch (error) {
        console.log(error)
      } finally {
        this.isTrackingLoading = false
      }
    },
    syncActiveTabFromRoute () {
      if (this.$route.query.tab && this.workspaceTabsWithCounts.some(item => item.id === this.$route.query.tab)) {
        this.activeTab = this.$route.query.tab
      }
    },
    async loadWorkspace () {
      const companyId = parseInt(this.$route.params.id)
      if (this.isInitialLoading) {
        this.company = { id: 0 }
        this.record = { id: 0 }
        this.dataQuality = {
          percentage: 0,
          completed_checks: 0,
          total_checks: 0,
          missing: [],
          tone: 'critical'
        }
        this.invoices = []
        this.addresses = []
        this.contracts = []
        this.bankAccounts = []
        this.secrets = []
        this.notes = []
        this.tasks = []
      } else {
        this.isRefreshing = true
      }

      try {
        const [workspace] = await Promise.all([
          companyService.getWorkspace(companyId),
          this.loadPeople(),
          this.loadOperations(),
          this.loadAccesses(),
          this.loadTracking()
        ])
        this.company = workspace.company
        this.record = workspace.record
        this.dataQuality = workspace.dataQuality || {
          percentage: 0,
          completed_checks: 0,
          total_checks: 0,
          missing: [],
          tone: 'critical'
        }
        this.invoices = workspace.invoices
        this.taxRegimes = workspace.taxRegimes
        this.billerTypes = workspace.billerTypes
        this.currencies = workspace.currencies
        this.activityFeed = workspace.recentActivity
        this.activityTotal = workspace.activityTotal
      } catch (error) {
        console.log(error)
      } finally {
        this.isInitialLoading = false
        this.isRefreshing = false
        this.syncActiveTabFromRoute()
      }
    },
    async loadRecordCatalogs () {
      if (!this.taxRegimes.length || !this.billerTypes.length) {
        this.isRecordCatalogLoading = true
        try {
          const workspace = await companyService.getWorkspace(parseInt(this.$route.params.id))
          this.taxRegimes = workspace.taxRegimes
          this.billerTypes = workspace.billerTypes
        } catch (error) {
          console.log(error)
        } finally {
          this.isRecordCatalogLoading = false
        }
      }
    },
    async openDrawer (type, entity = null) {
      let drawerEntity = entity
      if (type === 'contact' && entity && entity.id) {
        try {
          drawerEntity = await companyPeopleService.getContact(entity.id)
        } catch (error) {
          console.log(error)
        }
      }
      if (type === 'address' && entity && entity.id) {
        try {
          drawerEntity = await companyOperationsService.getAddress(entity.id)
        } catch (error) {
          console.log(error)
        }
      }
      if (type === 'contract' && entity && entity.id) {
        try {
          drawerEntity = await companyOperationsService.getContract(entity.id)
        } catch (error) {
          console.log(error)
        }
      }
      if (type === 'bank-account' && entity && entity.id) {
        try {
          drawerEntity = await companyOperationsService.getBankAccount(entity.id)
        } catch (error) {
          console.log(error)
        }
      }
      if (type === 'partner' && entity && entity.id) {
        try {
          drawerEntity = await companyPeopleService.getPartner(entity.id)
        } catch (error) {
          console.log(error)
        }
      }
      if (type === 'secret' && entity && entity.id) {
        try {
          drawerEntity = await companyAccessService.getSecret(entity.id)
        } catch (error) {
          console.log(error)
        }
      }
      if (type === 'legal-representative' && entity && entity.id) {
        try {
          drawerEntity = await companyPeopleService.getLegalRepresentative(entity.id)
        } catch (error) {
          console.log(error)
        }
      }
      if (type === 'other-relationship' && entity && entity.id) {
        try {
          drawerEntity = await companyPeopleService.getOtherRelationship(entity.id)
        } catch (error) {
          console.log(error)
        }
      }

      this.drawer = {
        open: true,
        type,
        mode: entity ? 'edit' : 'create',
        entity: drawerEntity
      }
      if (type === 'record') {
        await this.loadRecordCatalogs()
      }
    },
    closeDrawer () {
      this.drawer = {
        open: false,
        type: '',
        mode: 'create',
        entity: null
      }
    },
    async handleRecordSaved () {
      this.closeDrawer()
      await this.loadWorkspace()
    },
    async handleContactSaved () {
      this.closeDrawer()
      await this.loadPeople()
      await this.loadWorkspace()
    },
    async handleAddressSaved () {
      this.closeDrawer()
      await this.loadOperations()
      await this.loadWorkspace()
    },
    async handleBankAccountSaved () {
      this.closeDrawer()
      await this.loadOperations()
      await this.loadWorkspace()
    },
    handlePartnerProfileSaved (partner) {
      this.drawer = {
        ...this.drawer,
        mode: 'edit',
        entity: partner
      }
    },
    async handlePartnerProfileRefresh () {
      await this.loadPeople()
      await this.loadWorkspace()
    },
    async handleLegalRepresentativeSaved () {
      this.closeDrawer()
      await this.loadPeople()
      await this.loadWorkspace()
    },
    async handleOtherRelationshipSaved () {
      this.closeDrawer()
      await this.loadPeople()
      await this.loadWorkspace()
    },
    async handleContractSaved () {
      this.closeDrawer()
      await this.loadOperations()
      await this.loadWorkspace()
    },
    async handleSecretSaved () {
      this.closeDrawer()
      await this.loadAccesses()
      await this.loadWorkspace()
    },
    confirmDeleteContact (contact) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar contacto',
        message: `Se eliminará el contacto ${contact.name || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-contact',
        payload: contact
      }
    },
    confirmDeleteAddress (address) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar dirección',
        message: `Se eliminará la dirección ${address.name || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-address',
        payload: address
      }
    },
    confirmDeletePartner (partner) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar socio',
        message: `Se eliminará el socio ${partner.name || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-partner',
        payload: partner
      }
    },
    confirmDeleteLegalRepresentative (legalRepresentative) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar representante legal',
        message: `Se eliminará el representante legal ${legalRepresentative.name || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-legal-representative',
        payload: legalRepresentative
      }
    },
    confirmDeleteOtherRelationship (otherRelationship) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar otra relacion',
        message: `Se eliminará la relación ${otherRelationship.name || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-other-relationship',
        payload: otherRelationship
      }
    },
    confirmDeleteContract (contract) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar contrato',
        message: `Se eliminará el contrato ${contract.concept || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-contract',
        payload: contract
      }
    },
    confirmDeleteBankAccount (bankAccount) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar cuenta bancaria',
        message: `Se eliminará la cuenta ${bankAccount.bank_name || ''} ${bankAccount.account_number || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-bank-account',
        payload: bankAccount
      }
    },
    confirmDeleteSecret (secret) {
      this.confirmDialog = {
        open: true,
        title: 'Eliminar acceso',
        message: `Se eliminará el acceso ${secret.name || ''}. Esta acción no se puede deshacer.`,
        loading: false,
        action: 'delete-secret',
        payload: secret
      }
    },
    revealSecret (secret) {
      this.openReasonDialog(secret, 'reveal')
    },
    copySecret (secret) {
      this.openReasonDialog(secret, 'copy')
    },
    openReasonDialog (secret, action) {
      this.reasonDialog = {
        open: true,
        title: action === 'copy' ? `Copiar ${secret.name || 'acceso'}` : `Revelar ${secret.name || 'acceso'}`,
        message: 'Debes ingresar un motivo para continuar. Esta acción quedará registrada en la auditoría.',
        confirmLabel: action === 'copy' ? 'Copiar' : 'Revelar',
        action,
        secret,
        reason: '',
        password: '',
        error: '',
        loading: false
      }
    },
    closeReasonDialog (force = false) {
      if (this.reasonDialog.loading && !force) return
      this.reasonDialog = {
        open: false,
        title: '',
        message: '',
        confirmLabel: 'Confirmar',
        action: '',
        secret: null,
        reason: '',
        password: '',
        error: '',
        loading: false
      }
    },
    async submitReasonDialog () {
      const reason = (this.reasonDialog.reason || '').trim()
      const password = this.reasonDialog.password || ''
      if (!reason) {
        this.reasonDialog.error = 'Debes indicar un motivo.'
        return
      }
      if (!password) {
        this.reasonDialog.error = 'Debes confirmar tu contraseña.'
        return
      }
      this.reasonDialog.loading = true
      this.reasonDialog.error = ''
      try {
        const fullSecret = await companyAccessService.revealSecret(
          this.reasonDialog.secret.id,
          this.reasonDialog.action,
          reason,
          password
        )
        if (this.reasonDialog.action === 'copy') {
          const value = fullSecret.secret || ''
          if (navigator && navigator.clipboard && value) {
            await navigator.clipboard.writeText(value)
            this.confirmDialog = {
              open: true,
              title: 'Copiado',
              message: `El valor de ${this.reasonDialog.secret.name || 'este acceso'} fue copiado al portapapeles.`,
              loading: false,
              action: '',
              payload: null
            }
          } else {
            this.confirmDialog = {
              open: true,
              title: 'No fue posible copiar',
              message: 'El acceso no tiene un valor disponible para copiar.',
              loading: false,
              action: '',
              payload: null
            }
          }
        } else {
          this.confirmDialog = {
            open: true,
            title: `Acceso: ${this.reasonDialog.secret.name || 'Secreto'}`,
            message: fullSecret.secret || 'Sin valor disponible',
            loading: false,
            action: '',
            payload: null
          }
        }
        this.closeReasonDialog(true)
        await this.loadAccesses()
        await this.loadWorkspace()
        if (this.drawer.type === 'secret-audit-history') {
          await this.loadSecretAuditHistory(this.secretAuditHistoryPage)
        }
      } catch (error) {
        this.reasonDialog.error = error && error.message ? error.message : 'No fue posible completar la acción.'
      } finally {
        this.reasonDialog.loading = false
      }
    },
    formatSecretActionLabel (entry) {
      const action = entry && entry.payload ? entry.payload.action : ''
      if (action === 'copy') return 'Copia'
      if (action === 'export') return 'Exportacion'
      return 'Revelacion'
    },
    formatSecretScopeLabel (entry) {
      const scope = entry && entry.payload ? entry.payload.secret_scope : ''
      return scope === 'person' ? 'Persona relacionada' : 'Cliente'
    },
    getDownloadFilename (headers, fallbackName = 'descarga.xls') {
      const contentDisposition = headers['content-disposition'] || ''
      const match = contentDisposition.match(/filename="?([^"]+)"?/i)
      return match ? match[1] : fallbackName
    },
    async exportSecretAuditHistory () {
      this.isExportingSecretAudit = true
      try {
        const response = await companyService.exportSecretExposureAudit(
          parseInt(this.$route.params.id),
          this.includePeopleInSecretAudit
        )
        const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/vnd.ms-excel' })
        const exportUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = exportUrl
        link.download = this.getDownloadFilename(response.headers, 'auditoria_exposicion_secretos.xls')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(exportUrl)
      } catch (error) {
        console.log(error)
      } finally {
        this.isExportingSecretAudit = false
      }
    },

    goToRelatedTab () {
      this.activeTab = 'people'
    },

    closeConfirmDialog () {
      this.confirmDialog = {
        ...this.confirmDialog,
        open: false,
        loading: false,
        action: '',
        payload: null
      }
    },
    async executeConfirmAction () {
      if (this.confirmDialog.action === 'delete-contact' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyPeopleService.deleteContact(this.confirmDialog.payload.id)
          await this.loadPeople()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      if (this.confirmDialog.action === 'delete-address' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyOperationsService.deleteAddress(this.confirmDialog.payload.id)
          await this.loadOperations()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      if (this.confirmDialog.action === 'delete-partner' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyPeopleService.deletePartner(this.confirmDialog.payload.id)
          await this.loadPeople()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      if (this.confirmDialog.action === 'delete-legal-representative' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyPeopleService.deleteLegalRepresentative(this.confirmDialog.payload.id)
          await this.loadPeople()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      if (this.confirmDialog.action === 'delete-other-relationship' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyPeopleService.deleteOtherRelationship(this.confirmDialog.payload.id)
          await this.loadPeople()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      if (this.confirmDialog.action === 'delete-contract' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyOperationsService.deleteContract(this.confirmDialog.payload.id)
          await this.loadOperations()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      if (this.confirmDialog.action === 'delete-bank-account' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyOperationsService.deleteBankAccount(this.confirmDialog.payload.id)
          await this.loadOperations()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      if (this.confirmDialog.action === 'delete-secret' && this.confirmDialog.payload) {
        this.confirmDialog.loading = true
        try {
          await companyAccessService.deleteSecret(this.confirmDialog.payload.id)
          if (this.drawer.type === 'partner' && this.$refs.partnerProfileEditor) {
            await this.$refs.partnerProfileEditor.loadPersonalSecrets()
          }
          await this.loadAccesses()
          await this.loadWorkspace()
        } catch (error) {
          console.log(error)
        } finally {
          this.closeConfirmDialog()
        }
        return
      }

      this.closeConfirmDialog()
    },
    hasPermission (code) {
      const result = auth.hasUserPermission(code)
      return result
    }
  },
  mounted () {
    this.syncActiveTabFromRoute()
    this.loadWorkspace()
  },
  watch: {
    '$route.params.id' () {
      this.loadWorkspace()
    },
    '$route.query' () {
      this.syncActiveTabFromRoute()
    }
  }
}
</script>
<style scoped>
.company-view-shell {
  position: relative;
  max-width: 1380px;
  margin: 0 auto;
  padding: 1.25rem 1rem 2.5rem;
}

.company-workspace__body {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 1rem;
}

.company-workspace__main {
  min-width: 0;
}

.company-workspace__content {
  margin-bottom: 1rem;
}

.company-view-content--loading {
  opacity: 0.38;
  filter: saturate(0.78);
  pointer-events: none;
  transition: opacity 0.2s ease, filter 0.2s ease;
}

.company-view-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-align: center;
}

.company-view-loader--inline {
  min-height: 320px;
}

.company-view-loader--overlay {
  position: absolute;
  inset: 2rem 0 auto;
  z-index: 2;
}

.company-activity-history {
  display: grid;
  gap: 0.9rem;
}

.company-activity-history__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.company-activity-history__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  color: rgba(45, 35, 26, 0.72);
}

.company-activity-history__list {
  display: grid;
  gap: 0.75rem;
}

.company-activity-history__item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(95, 76, 52, 0.08);
}

.company-activity-history__item:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.company-activity-history__title {
  margin: 0;
  color: #241c16;
  font-weight: 600;
}

.company-activity-history__meta,
.company-activity-history__date,
.company-activity-history__state,
.company-activity-history__page {
  margin: 0;
  color: rgba(45, 35, 26, 0.58);
}

.company-activity-history__meta {
  margin-top: 0.18rem;
  font-size: 0.88rem;
}

.company-activity-history__date {
  white-space: nowrap;
  font-size: 0.82rem;
}

.company-activity-history__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.company-activity-history__button {
  min-height: 2.5rem;
  padding: 0 0.95rem;
  border: 1px solid rgba(89, 72, 55, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  color: #2b2118;
  font-weight: 600;
}

.company-activity-history__button:disabled {
  opacity: 0.45;
}

.confirm-dialog {
  position: fixed;
  inset: 0;
  z-index: 750;
  display: grid;
  place-items: center;
  padding: 1rem;
}

.confirm-dialog__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(21, 17, 13, 0.46);
  backdrop-filter: blur(6px);
}

.confirm-dialog__panel {
  position: relative;
  width: min(520px, calc(100vw - 2rem));
  border: 1px solid rgba(255, 255, 255, 0.56);
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 252, 247, 0.99), rgba(246, 239, 231, 0.97));
  box-shadow: 0 30px 70px rgba(28, 22, 16, 0.18);
  overflow: hidden;
}

.confirm-dialog__head,
.confirm-dialog__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.9rem;
}

.confirm-dialog__body {
  padding: 0 1rem 1rem;
}

.confirm-dialog__eyebrow {
  margin: 0 0 0.3rem;
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(58, 46, 33, 0.48);
}

.confirm-dialog__title {
  margin: 0;
  color: #211913;
  font-size: 1.2rem;
}

.confirm-dialog__message {
  margin: 0 0 0.8rem;
  color: #4a3a2d;
  line-height: 1.45;
}

.confirm-dialog__close {
  width: 2.2rem;
  height: 2.2rem;
  border: 1px solid rgba(102, 81, 57, 0.12);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.72);
  color: #392d22;
  flex-shrink: 0;
}

.confirm-dialog__foot {
  justify-content: flex-end;
  padding-top: 0;
  padding-bottom: 1rem;
}

.confirm-dialog__ghost,
.confirm-dialog__danger {
  min-height: 2.5rem;
  padding: 0 0.9rem;
  border-radius: 999px;
}

.confirm-dialog__ghost {
  border: 1px solid rgba(102, 81, 57, 0.12);
  background: rgba(255, 255, 255, 0.7);
  color: #392d22;
}

.confirm-dialog__danger {
  border: 0;
  background: #b95c5c;
  color: #fff8f8;
}

.confirm-dialog-fade-enter-active,
.confirm-dialog-fade-leave-active {
  transition: opacity 0.18s ease;
}

.confirm-dialog-fade-enter-from,
.confirm-dialog-fade-leave-to {
  opacity: 0;
}

.company-view-reason__textarea {
  width: 100%;
  min-height: 7rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid rgba(102, 81, 57, 0.18);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.78);
  color: #2d231a;
  resize: vertical;
}

.company-view-reason__input {
  width: 100%;
  margin-top: 0.85rem;
  padding: 0.8rem 0.9rem;
  border: 1px solid rgba(102, 81, 57, 0.18);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.78);
  color: #2d231a;
}

.company-view-reason__error {
  margin: 0.65rem 0 0;
  color: #b95252;
  font-size: 0.92rem;
}
.company-view-loader-title {
  margin: 0;
  color: #162033;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.company-view-loader-copy {
  max-width: 22rem;
  margin: 0;
  color: rgba(36, 31, 25, 0.56);
  font-size: 0.9rem;
}

.company-view-spinner {
  position: relative;
  width: 56px;
  height: 56px;
}

.company-view-spinner--compact {
  width: 42px;
  height: 42px;
}

.company-view-spinner-ring,
.company-view-spinner-core {
  position: absolute;
  inset: 0;
  border-radius: 50%;
}

.company-view-spinner-ring {
  border: 1.5px solid rgba(36, 31, 25, 0.08);
  border-top-color: #33281d;
  border-right-color: rgba(74, 60, 43, 0.35);
  animation: company-view-spin 0.9s cubic-bezier(0.65, 0.05, 0.36, 1) infinite;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.company-view-spinner-core {
  inset: 10px;
  background: radial-gradient(circle at 30% 30%, #ffffff, #ece2d5);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

@keyframes company-view-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (min-width: 1600px) and (max-width: 1799px) {
  .company-view-shell {
    max-width: 1390px;
    padding-top: 1rem;
  }

  .company-workspace__body {
    grid-template-columns: 252px minmax(0, 1fr);
    gap: 1.05rem;
  }
}

@media (min-width: 1800px) {
  .company-view-shell {
    max-width: 1460px;
    padding-top: 1rem;
  }

  .company-workspace__body {
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 1.15rem;
  }
}

@media (max-width: 980px) {
  .company-workspace__body {
    grid-template-columns: 1fr;
  }

  .company-activity-history__item {
    flex-direction: column;
  }

  .company-activity-history__date {
    white-space: normal;
  }
}

@media (max-width: 640px) {
  .company-view-shell {
    padding: 1rem 0.75rem 2rem;
  }

  .company-activity-history__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .company-activity-history__button {
    width: 100%;
  }
}
</style>
